import * as React from 'react'

import { cn } from './cn'

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  size?: 'small' | 'medium' | 'large'
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'info'
    | 'success'
    | 'inherit'
  /** Ignored; kept for MUI call-site compatibility. */
  edge?: 'start' | 'end' | false
  href?: string
  target?: string
  rel?: string
  component?: React.ElementType
}

const sizeClasses = {
  small: 'p-1.5 text-xl rounded-lg',
  medium: 'p-2 text-2xl rounded-field',
  large: 'p-2.5 text-3xl rounded-field',
}

const colorClasses = {
  default: 'text-text-secondary hover:text-text-primary',
  inherit: 'text-inherit',
  primary: 'text-primary',
  secondary: 'text-primary',
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
  success: 'text-success',
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      size = 'medium',
      color = 'default',
      edge,
      className,
      children,
      href,
      component,
      ...props
    },
    ref,
  ) => {
    const Component: React.ElementType = component || (href ? 'a' : 'button')
    return (
      <Component
        ref={ref}
        href={href}
        type={Component === 'button' ? 'button' : undefined}
        className={cn(
          'inline-flex items-center justify-center cursor-pointer select-none',
          'transition-all duration-150 active:scale-95',
          'hover:bg-black/[0.05] dark:hover:bg-white/[0.07]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          'disabled:pointer-events-none disabled:opacity-40',
          sizeClasses[size],
          colorClasses[color],
          edge === 'start' && '-ml-2',
          edge === 'end' && '-mr-2',
          className,
        )}
        {...props}
      >
        {children}
      </Component>
    )
  },
)
IconButton.displayName = 'IconButton'

export default IconButton
